import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header/Header';
import { ForumTopicListResponse } from '../../services/forumService';

interface ForumMobileProps {
    data: ForumTopicListResponse | null;
    loading: boolean;
    activeFilter: string;
    setActiveFilter: (filter: string) => void;
    handleDeleteTopic: (e: React.MouseEvent, topicId: number) => void;
    formatDate: (dateStr: string) => string;
    CATEGORY_CONFIG: any;
    user: any;
}

const ForumMobile: React.FC<ForumMobileProps> = ({
    data,
    loading,
    activeFilter,
    setActiveFilter,
    handleDeleteTopic,
    formatDate,
    CATEGORY_CONFIG,
    user
}) => {
    const navigate = useNavigate();
    const getCat = (category: string) => CATEGORY_CONFIG[category] || CATEGORY_CONFIG.GENERAL;
    const filteredTopics = data?.topics.filter(t => activeFilter === 'ALL' || t.category === activeFilter) || [];
    const filters = ['ALL', ...Object.keys(CATEGORY_CONFIG)];

    return (
        <div style={{ paddingBottom: '80px' }}>
            {/* Standard Header with Menu Burger */}
            <Header title="Discussions" icon="forum" />

            <div style={{ padding: '16px' }}>
                {/* Stats KPIs - Compact horizontal scroll */}
                {data && (
                    <div style={{ 
                        display: 'flex', 
                        gap: '12px', 
                        marginBottom: '20px', 
                        overflowX: 'auto', 
                        paddingBottom: '8px',
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none'
                    }}>
                        {[
                            { label: 'Sujets', value: data.total_count, icon: '📋', color: '#8b5cf6' },
                            { label: 'Réponses', value: data.topics.reduce((s, t) => s + t.replies_count, 0), icon: '💭', color: '#06b6d4' },
                            { label: 'Vues', value: data.topics.reduce((s, t) => s + t.views_count, 0), icon: '👁️', color: '#f59e0b' },
                        ].map((stat, i) => (
                            <div key={i} style={{
                                minWidth: '120px',
                                flex: '0 0 auto',
                                background: 'rgba(255,255,255,0.03)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '12px',
                                padding: '12px',
                                display: 'flex', 
                                flexDirection: 'column',
                                gap: '4px',
                            }}>
                                <span style={{ fontSize: '20px' }}>{stat.icon}</span>
                                <div style={{ color: stat.color, fontSize: '18px', fontWeight: 800 }}>{stat.value}</div>
                                <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>{stat.label}</div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Filter Pills - Horizontal Scroll */}
                <div style={{ 
                    display: 'flex', 
                    gap: '8px', 
                    marginBottom: '20px', 
                    overflowX: 'auto', 
                    paddingBottom: '4px',
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none'
                }}>
                    {filters.map(f => {
                        const isActive = activeFilter === f;
                        const cat = f === 'ALL' ? { label: 'Tous', color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.1)' } : getCat(f);
                        return (
                            <button
                                key={f}
                                onClick={() => setActiveFilter(f)}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '20px',
                                    border: `1px solid ${isActive ? cat.color : 'transparent'}`,
                                    background: isActive ? cat.bg : 'rgba(255,255,255,0.03)',
                                    color: isActive ? cat.color : 'var(--text-muted)',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    fontWeight: isActive ? 700 : 500,
                                    whiteSpace: 'nowrap',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                {cat.label}
                            </button>
                        );
                    })}
                </div>

                {/* Topics List as Vertical Cards */}
                {loading ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
                        <div style={{ fontSize: '32px', marginBottom: '12px' }}>💬</div>
                        Chargement...
                    </div>
                ) : filteredTopics.length === 0 ? (
                    <div style={{
                        padding: '40px', textAlign: 'center',
                        background: 'rgba(255,255,255,0.02)',
                        borderRadius: '16px',
                        border: '1px dashed var(--border-color)',
                    }}>
                        <div style={{ fontSize: '32px', marginBottom: '12px' }}>🗣️</div>
                        <div style={{ color: 'var(--text-main)', fontWeight: 700 }}>Aucune discussion</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Soyez le premier !</div>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {filteredTopics.map((topic) => {
                            const cat = getCat(topic.category);
                            return (
                                <div
                                    key={topic.id}
                                    onClick={() => navigate(`/forum/topic/${topic.id}`)}
                                    style={{
                                        background: 'rgba(255,255,255,0.02)',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: '16px',
                                        padding: '16px',
                                        position: 'relative',
                                        minHeight: '100px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '12px'
                                    }}
                                >
                                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                                        {/* Icon */}
                                        <div style={{
                                            width: '40px', height: '40px', borderRadius: '12px', flexShrink: 0,
                                            background: `linear-gradient(135deg, ${cat.color}33, ${cat.color}11)`,
                                            border: `1px solid ${cat.color}44`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '18px',
                                        }}>
                                            {cat.icon}
                                        </div>
                                        
                                        {/* Title & Metadata */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ 
                                                color: 'var(--text-main)', 
                                                fontWeight: 700, 
                                                fontSize: '15px',
                                                marginBottom: '4px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '4px'
                                            }}>
                                                {topic.title}
                                                {topic.stock_symbol && (
                                                    <span style={{
                                                        alignSelf: 'flex-start',
                                                        backgroundColor: 'rgba(56, 189, 248, 0.15)',
                                                        color: '#38bdf8',
                                                        padding: '2px 8px',
                                                        borderRadius: '6px',
                                                        fontSize: '10px',
                                                        fontWeight: 700,
                                                    }}>
                                                        ${topic.stock_symbol}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Bottom Info Row */}
                                    <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center', 
                                        paddingTop: '8px',
                                        borderTop: '0.1px solid rgba(255,255,255,0.05)'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--text-muted)' }}>
                                            <span style={{
                                                width: '18px', height: '18px', borderRadius: '5px',
                                                background: `linear-gradient(135deg, ${cat.color}, ${cat.color}88)`,
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                color: 'white', fontWeight: 700, fontSize: '10px',
                                            }}>
                                                {topic.author_name.charAt(0).toUpperCase()}
                                            </span>
                                            {topic.author_name}
                                            <span style={{ fontSize: '10px' }}>•</span>
                                            {formatDate(topic.last_activity_at)}
                                        </div>
                                        
                                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <span style={{ color: topic.replies_count > 0 ? '#06b6d4' : 'var(--text-muted)', fontWeight: 700, fontSize: '13px' }}>
                                                    {topic.replies_count}
                                                </span>
                                                <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>💭</span>
                                            </div>
                                            {user?.id === topic.author_id && (
                                                <button
                                                    onClick={(e) => handleDeleteTopic(e, topic.id)}
                                                    style={{
                                                        background: 'transparent', border: 'none', color: '#f43f5e',
                                                        padding: '4px',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    🗑️
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Floating Action Button */}
            <button
                onClick={() => navigate('/forum/new')}
                style={{
                    position: 'fixed',
                    bottom: '24px',
                    right: '24px',
                    width: '56px',
                    height: '56px',
                    borderRadius: '28px',
                    background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                    color: 'white',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    boxShadow: '0 8px 16px rgba(99, 102, 241, 0.4)',
                    zIndex: 100,
                }}
            >
                ✏️
            </button>
        </div>
    );
};

export default ForumMobile;
